import React, {useEffect, useRef, useState} from 'react';
import {RefreshControl, ScrollView, StyleSheet, View} from 'react-native';
import {observer} from 'mobx-react';

import FeedTile from '../../../components/articleTile/FeedTile';
import {AirtableContentItem} from '../../../components/articleTile/models';
import HeroCarousel from '../../../components/HeroCarousel';
import LoadingImg from '../../../components/LoadingImg';
import ScrollToTopButton from '../../../components/ScrollToTopButton';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import WebFooter from '../../../components/WebFooter';
import {useWebScreenDimensions} from '../../../hooks/useWebScreenDimensions';
import {useStores} from '../../../stores/use-stores';
import {chunkIntoN} from '../../../utils/functions';

type ChunkedData = {
  leftChunk: any[];
  rightChunk: any[];
  middleChunks: any[];
};

const chunk = (arr: any[], n: number): ChunkedData => {
  const chunks = chunkIntoN(arr, n);
  const leftChunk = chunks[0];
  const rightChunk = chunks[chunks.length - 1];
  const middleChunks = chunks.slice(1, chunks.length - 1);
  return {
    leftChunk,
    rightChunk,
    middleChunks,
  };
};

type FeedListProps = {
  canStorePickup: boolean;
  canStoreDeliver: boolean;
  isStoreLoading: boolean;
};

const FeedList = observer(
  ({canStorePickup, canStoreDeliver, isStoreLoading}: FeedListProps) => {
    const {feedStore, promotionsStore: activationsStore} = useStores();
    const {isWebDesktopScreen} = useWebScreenDimensions();

    // Scroll button stuff
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [feedData, setFeedData] = useState<AirtableContentItem[]>([]);
    const listRef = useRef();

    useEffect(() => {
      const index = Math.ceil(
        Math.random() * (feedStore?.feedList?.length || 0),
      );

      let activations = activationsStore.feedData;
      if (canStorePickup || canStoreDeliver) {
        activations = [
          ...activations,
          ...activationsStore.listedECommerceOnlyPromotions,
        ];
      }
      const munged = [
        ...feedStore.feedList.slice(0, index),
        ...activations,
        ...feedStore.feedList.slice(index),
      ];
      setFeedData(munged);
    }, [
      activationsStore.listedECommerceOnlyPromotions,
      activationsStore.feedData,
      feedStore.feedList,
      canStorePickup,
      canStoreDeliver,
    ]);

    const refresh = () => feedStore.loadFeedList();

    const numberOfColumns = isWebDesktopScreen ? 3 : 1;
    const chunkedData: ChunkedData =
      numberOfColumns > 1
        ? chunk(feedData, numberOfColumns)
        : {
            leftChunk: [],
            middleChunks: [],
            rightChunk: [],
          };

    const renderFeedTile = (item: AirtableContentItem) => (
      <FeedTile item={item} key={item.id} />
    );

    return (
      // The hero carousel has an extra 8 padding
      <ContentView testID="feed-list-screen" style={{paddingHorizontal: 8}}>
        {isStoreLoading ? (
          <LoadingImg />
        ) : (
          <>
            <ScrollView
              refreshControl={
                <RefreshControl refreshing={false} onRefresh={refresh} />
              }
              showsVerticalScrollIndicator={isWebDesktopScreen}
              showsHorizontalScrollIndicator={isWebDesktopScreen}
              // Handle scroll to top button
              onScrollBeginDrag={() => setShowScrollButton(true)}
              onMomentumScrollBegin={() => setShowScrollButton(true)}
              onMomentumScrollEnd={() => setShowScrollButton(false)}
              onScrollEndDrag={() => setShowScrollButton(false)}
              // @ts-ignore
              ref={listRef}>
              <HeroCarousel />
              {isWebDesktopScreen ? (
                // on desktop screen on web, 3 (or more if wanted) columns but not like a grid given
                // that the tiles have different heights and need to look contiguous vertically
                <View
                  style={styles.columns}
                  testID="fed-list-screen-content-columns">
                  <View
                    key="left"
                    style={styles.column}
                    testID="feed-list-screen-content-left-column">
                    {chunkedData.leftChunk.map(renderFeedTile)}
                  </View>
                  {chunkedData.middleChunks.map((middleChunk, i) => (
                    <View
                      style={styles.column}
                      key={`middle-${i}`}
                      testID="feed-list-screen-content-middle-column">
                      {middleChunk.map(renderFeedTile)}
                    </View>
                  ))}
                  <View
                    key="right"
                    style={styles.column}
                    testID="feed-list-screen-content-right-column">
                    {chunkedData.rightChunk.map(renderFeedTile)}
                  </View>
                </View>
              ) : (
                // on small/mobile screens, just one column
                feedData.map(renderFeedTile)
              )}
              {!!isWebDesktopScreen && <WebFooter />}
            </ScrollView>
            <ScrollToTopButton
              open={showScrollButton}
              callback={() =>
                // @ts-ignore
                listRef.current?.scrollTo({x: 0, y: 0, animated: true})
              }
            />
          </>
        )}
      </ContentView>
    );
  },
);

export default FeedList;

const styles = StyleSheet.create({
  columns: {
    flexDirection: 'row',
    flex: 1,
  },
  column: {
    flex: 1,
  },
});
